import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import {
  IconArrowBackUp,
  IconArrowDown,
  IconArrowUp,
  IconCheck,
  IconPencil,
  IconPlus,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { useState } from 'react';
import PageLoader from 'src/UI/loader/PageLoader';

import LlmConfigHistory from './LlmConfigHistory';
import {
  type LlmConfigItem,
  useLlmConfig,
  useResetLlmConfig,
  useUpdateLlmConfig,
} from './hooks/useLlmConfig';

type FallbackModel = { name: string; provider: string };

function parseFallbackModels(modelName: string): FallbackModel[] {
  try {
    return JSON.parse(modelName);
  } catch {
    return [];
  }
}

export default function LlmConfigTab() {
  const { configs, isLoading, refetch } = useLlmConfig();
  const [editingSlot, setEditingSlot] = useState<string | null>(null);

  if (isLoading) {
    return <PageLoader />;
  }

  const singleModelConfigs =
    configs?.filter(c => !c.slotKey.startsWith('llm-fallback-routes.')) ?? [];
  const fallbackRouteConfigs =
    configs?.filter(c => c.slotKey.startsWith('llm-fallback-routes.')) ?? [];

  return (
    <Stack gap="lg">
      <Box>
        <Text fw={600} size="lg">
          LLM Model Configuration
        </Text>
        <Text size="sm" c="dimmed">
          Override model configurations without redeploying. Changes take effect within 60 seconds.
        </Text>
      </Box>

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Slot</Table.Th>
            <Table.Th>Model</Table.Th>
            <Table.Th>Provider</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {singleModelConfigs.map(config => (
            <SingleModelRow
              key={config.slotKey}
              config={config}
              isEditing={editingSlot === config.slotKey}
              onEdit={() => setEditingSlot(config.slotKey)}
              onCancel={() => setEditingSlot(null)}
              onSaved={() => {
                setEditingSlot(null);
                refetch();
              }}
            />
          ))}
        </Table.Tbody>
      </Table>

      {fallbackRouteConfigs.length > 0 && (
        <Stack gap="md">
          <Box>
            <Text fw={600} size="md">
              Fallback Routes
            </Text>
            <Text size="sm" c="dimmed">
              Ordered list of models to try. If the first model fails, the next is used.
            </Text>
          </Box>
          {fallbackRouteConfigs.map(config => (
            <FallbackRouteCard key={config.slotKey} config={config} onSaved={refetch} />
          ))}
        </Stack>
      )}

      <LlmConfigHistory />
    </Stack>
  );
}

function SingleModelRow({
  config,
  isEditing,
  onEdit,
  onCancel,
  onSaved,
}: {
  config: LlmConfigItem;
  isEditing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [modelName, setModelName] = useState(config.modelName);
  const [provider, setProvider] = useState(config.provider);
  const { updateConfig, isLoading: isUpdating } = useUpdateLlmConfig();
  const { resetConfig, isLoading: isResetting } = useResetLlmConfig();

  const handleSave = async () => {
    await updateConfig(config.slotKey, modelName, provider);
    onSaved();
  };

  const handleReset = async () => {
    await resetConfig(config.slotKey);
    onSaved();
  };

  const handleStartEdit = () => {
    setModelName(config.modelName);
    setProvider(config.provider);
    onEdit();
  };

  if (isEditing) {
    return (
      <Table.Tr>
        <Table.Td>
          <Text size="sm" ff="monospace">
            {config.slotKey}
          </Text>
        </Table.Td>
        <Table.Td>
          <TextInput
            size="xs"
            value={modelName}
            onChange={e => setModelName(e.currentTarget.value)}
          />
        </Table.Td>
        <Table.Td>
          <TextInput
            size="xs"
            value={provider}
            onChange={e => setProvider(e.currentTarget.value)}
          />
        </Table.Td>
        <Table.Td />
        <Table.Td>
          <Group gap="xs">
            <ActionIcon
              variant="filled"
              color="green"
              size="sm"
              onClick={handleSave}
              loading={isUpdating}
            >
              <IconCheck size={14} />
            </ActionIcon>
            <ActionIcon variant="subtle" color="gray" size="sm" onClick={onCancel}>
              <IconX size={14} />
            </ActionIcon>
          </Group>
        </Table.Td>
      </Table.Tr>
    );
  }

  return (
    <Table.Tr>
      <Table.Td>
        <Text size="sm" ff="monospace">
          {config.slotKey}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{config.modelName}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{config.provider}</Text>
      </Table.Td>
      <Table.Td>
        {config.isOverridden ? (
          <Badge color="blue" size="sm">
            Overridden
          </Badge>
        ) : (
          <Badge color="gray" size="sm" variant="light">
            Default
          </Badge>
        )}
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <ActionIcon variant="subtle" size="sm" onClick={handleStartEdit}>
            <IconPencil size={14} />
          </ActionIcon>
          {config.isOverridden && (
            <ActionIcon
              variant="subtle"
              color="orange"
              size="sm"
              onClick={handleReset}
              loading={isResetting}
            >
              <IconArrowBackUp size={14} />
            </ActionIcon>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

function FallbackRouteCard({ config, onSaved }: { config: LlmConfigItem; onSaved: () => void }) {
  const modality = config.slotKey.replace('llm-fallback-routes.', '');
  const currentModels = parseFallbackModels(config.modelName);

  const [isEditing, setIsEditing] = useState(false);
  const [editModels, setEditModels] = useState<FallbackModel[]>([]);
  const { updateConfig, isLoading: isUpdating } = useUpdateLlmConfig();
  const { resetConfig, isLoading: isResetting } = useResetLlmConfig();

  const handleStartEdit = () => {
    setEditModels(currentModels.map(m => ({ ...m })));
    setIsEditing(true);
  };

  const handleSave = async () => {
    const serialized = JSON.stringify(editModels);
    await updateConfig(config.slotKey, serialized, '__list__');
    setIsEditing(false);
    onSaved();
  };

  const handleReset = async () => {
    await resetConfig(config.slotKey);
    setIsEditing(false);
    onSaved();
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const moveModel = (index: number, direction: 'up' | 'down') => {
    const newModels = [...editModels];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newModels[index], newModels[swapIndex]] = [newModels[swapIndex], newModels[index]];
    setEditModels(newModels);
  };

  const removeModel = (index: number) => {
    setEditModels(editModels.filter((_, i) => i !== index));
  };

  const addModel = () => {
    setEditModels([...editModels, { name: '', provider: '' }]);
  };

  const updateModel = (index: number, field: keyof FallbackModel, value: string) => {
    const newModels = [...editModels];
    newModels[index] = { ...newModels[index], [field]: value };
    setEditModels(newModels);
  };

  const displayModels = isEditing ? editModels : currentModels;

  return (
    <Card withBorder p="md">
      <Group justify="space-between" mb="sm">
        <Group gap="sm">
          <Text fw={600} size="sm" tt="capitalize">
            {modality}
          </Text>
          {config.isOverridden ? (
            <Badge color="blue" size="sm">
              Overridden
            </Badge>
          ) : (
            <Badge color="gray" size="sm" variant="light">
              Default
            </Badge>
          )}
        </Group>
        <Group gap="xs">
          {!isEditing ? (
            <>
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconPencil size={14} />}
                onClick={handleStartEdit}
              >
                Edit
              </Button>
              {config.isOverridden && (
                <Button
                  variant="subtle"
                  size="xs"
                  color="orange"
                  leftSection={<IconArrowBackUp size={14} />}
                  onClick={handleReset}
                  loading={isResetting}
                >
                  Reset
                </Button>
              )}
            </>
          ) : (
            <>
              <Button
                variant="filled"
                size="xs"
                color="green"
                onClick={handleSave}
                loading={isUpdating}
              >
                Save
              </Button>
              <Button variant="subtle" size="xs" color="gray" onClick={handleCancel}>
                Cancel
              </Button>
            </>
          )}
        </Group>
      </Group>

      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={50}>#</Table.Th>
            <Table.Th>Model</Table.Th>
            <Table.Th>Provider</Table.Th>
            {isEditing && <Table.Th w={120}>Actions</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {displayModels.map((model, index) => (
            <Table.Tr key={index}>
              <Table.Td>
                <Text size="sm" c="dimmed">
                  {index + 1}
                </Text>
              </Table.Td>
              <Table.Td>
                {isEditing ? (
                  <TextInput
                    size="xs"
                    value={model.name}
                    onChange={e => updateModel(index, 'name', e.currentTarget.value)}
                  />
                ) : (
                  <Text size="sm">{model.name}</Text>
                )}
              </Table.Td>
              <Table.Td>
                {isEditing ? (
                  <TextInput
                    size="xs"
                    value={model.provider}
                    onChange={e => updateModel(index, 'provider', e.currentTarget.value)}
                  />
                ) : (
                  <Text size="sm">{model.provider}</Text>
                )}
              </Table.Td>
              {isEditing && (
                <Table.Td>
                  <Group gap={4}>
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      disabled={index === 0}
                      onClick={() => moveModel(index, 'up')}
                    >
                      <IconArrowUp size={14} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      disabled={index === displayModels.length - 1}
                      onClick={() => moveModel(index, 'down')}
                    >
                      <IconArrowDown size={14} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="sm"
                      onClick={() => removeModel(index)}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {isEditing && (
        <Button
          variant="subtle"
          size="xs"
          mt="xs"
          leftSection={<IconPlus size={14} />}
          onClick={addModel}
        >
          Add model
        </Button>
      )}
    </Card>
  );
}
